import React, { useState, useRef, useEffect, useCallback } from "react";
import { ArrowLeftRight, ChevronLeft, ChevronRight } from "lucide-react";
import { BEFORE_AFTER as DEFAULT_BEFORE_AFTER } from "../data/content";
import axios from "axios";

const BeforeAfter = () => {
  const [items, setItems] = useState(DEFAULT_BEFORE_AFTER);
  const [active, setActive] = useState(0);
  const [pos, setPos] = useState(50);
  const [dragging, setDragging] = useState(false);
  const containerRef = useRef(null);
  const draggingRef = useRef(false);

  useEffect(() => {
    axios.get("/api/site/content")
      .then((res) => {
        if (res.data?.before_after && Array.isArray(res.data.before_after) && res.data.before_after.length > 0) {
          setItems(res.data.before_after);
        }
      })
      .catch(() => {});
  }, []);

  const total = items.length;
  const safeActive = Math.min(active, Math.max(0, total - 1));
  const current = items[safeActive] || items[0] || DEFAULT_BEFORE_AFTER[0];

  const updatePos = useCallback((clientX) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = clientX - rect.left;
    const next = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setPos(next);
  }, []);

  const handleStart = (e) => {
    draggingRef.current = true;
    setDragging(true);
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    updatePos(x);
  };

  useEffect(() => {
    const move = (e) => {
      if (!draggingRef.current) return;
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      updatePos(x);
    };
    const end = () => {
      draggingRef.current = false;
      setDragging(false);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", end);
    window.addEventListener("touchmove", move, { passive: true });
    window.addEventListener("touchend", end);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", end);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", end);
    };
  }, [updatePos]);

  useEffect(() => {
    setPos(50);
  }, [safeActive]);

  const goPrev = () => setActive((safeActive - 1 + total) % total);
  const goNext = () => setActive((safeActive + 1) % total);

  return (
    <section
      id="before-after"
      className="section-y border-t-2 border-burgundy relative overflow-hidden"
      data-testid="before-after-section"
    >
      <div className="pointer-events-none select-none absolute -top-4 -left-6 font-heading text-[18vw] leading-none text-burgundy/5 hidden md:block">
        Работы
      </div>

      <div className="container-x relative">
        <div className="flex items-end justify-between gap-8 mb-12 md:mb-16">
          <div>
            <h2 className="font-heading text-5xl md:text-7xl lg:text-8xl text-ink heading-tight">
              До — <span className="accent text-burgundy">после.</span>
            </h2>
            {current.title && (
              <p className="mt-2 text-sm md:text-base font-semibold text-burgundy">
                {current.title} {current.note ? `— ${current.note}` : ''}
              </p>
            )}
          </div>
          <div
            className="hidden md:flex items-baseline gap-2 font-heading text-burgundy"
            data-testid="ba-counter"
          >
            <span className="text-5xl">{String(safeActive + 1).padStart(2, "0")}</span>
            <span className="text-2xl text-burgundy/40">/ {String(total).padStart(2, "0")}</span>
          </div>
        </div>

        <div className="relative">
          <div className="absolute -top-3 -left-3 right-3 bottom-3 border-2 border-burgundy -z-10 hidden md:block" />

          <div
            ref={containerRef}
            className={`relative w-full aspect-[5/4] md:aspect-[16/10] overflow-hidden bg-burgundy-deep border-2 border-burgundy select-none touch-none ${dragging ? "cursor-grabbing" : "cursor-ew-resize"}`}
            onMouseDown={handleStart}
            onTouchStart={handleStart}
            data-testid="ba-slider"
          >
            <img
              key={`after-${safeActive}`}
              src={current.after}
              alt=""
              draggable={false}
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            />

            <img
              key={`before-${safeActive}`}
              src={current.before}
              alt=""
              draggable={false}
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
              style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
            />

            <div className="absolute top-4 left-4 md:top-6 md:left-6 px-3 py-1.5 bg-cream-paper text-burgundy text-[10px] md:text-xs uppercase tracking-[0.28em] font-bold border-2 border-burgundy pointer-events-none">
              До
            </div>
            <div className="absolute top-4 right-4 md:top-6 md:right-6 px-3 py-1.5 bg-burgundy text-cream text-[10px] md:text-xs uppercase tracking-[0.28em] font-bold border-2 border-burgundy pointer-events-none">
              После
            </div>

            <div
              className="absolute top-0 bottom-0 w-[2px] bg-cream-paper pointer-events-none"
              style={{ left: `${pos}%`, transform: "translateX(-50%)" }}
            >
              <div
                className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 bg-cream-paper border-2 border-burgundy flex items-center justify-center transition-shadow ${dragging ? "shadow-[0_0_0_4px_rgba(74,20,25,0.18)]" : "shadow-[0_4px_0_0_#4A1419]"}`}
              >
                <ArrowLeftRight size={20} className="text-burgundy" strokeWidth={2.5} />
              </div>
            </div>
          </div>

          <button
            onClick={goPrev}
            aria-label="Предыдущая пара"
            className="hidden md:flex absolute top-1/2 -translate-y-1/2 -left-6 lg:-left-8 w-12 h-12 lg:w-14 lg:h-14 bg-cream border-2 border-burgundy text-burgundy hover:bg-burgundy hover:text-cream transition-colors items-center justify-center z-10"
            data-testid="ba-prev"
          >
            <ChevronLeft size={22} strokeWidth={2} />
          </button>
          <button
            onClick={goNext}
            aria-label="Следующая пара"
            className="hidden md:flex absolute top-1/2 -translate-y-1/2 -right-6 lg:-right-8 w-12 h-12 lg:w-14 lg:h-14 bg-cream border-2 border-burgundy text-burgundy hover:bg-burgundy hover:text-cream transition-colors items-center justify-center z-10"
            data-testid="ba-next"
          >
            <ChevronRight size={22} strokeWidth={2} />
          </button>
        </div>

        <div className="md:hidden mt-6 flex items-center justify-between">
          <button
            onClick={goPrev}
            aria-label="Предыдущая"
            className="w-12 h-12 bg-cream-paper border-2 border-burgundy text-burgundy flex items-center justify-center"
            data-testid="ba-prev-mobile"
          >
            <ChevronLeft size={20} strokeWidth={2} />
          </button>
          <div className="font-heading text-burgundy text-3xl flex items-baseline gap-1.5">
            <span>{String(safeActive + 1).padStart(2, "0")}</span>
            <span className="text-base text-burgundy/40">/ {String(total).padStart(2, "0")}</span>
          </div>
          <button
            onClick={goNext}
            aria-label="Следующая"
            className="w-12 h-12 bg-cream-paper border-2 border-burgundy text-burgundy flex items-center justify-center"
            data-testid="ba-next-mobile"
          >
            <ChevronRight size={20} strokeWidth={2} />
          </button>
        </div>

        <div className="mt-8 md:mt-10 flex gap-3 md:gap-4 overflow-x-auto pb-2 -mx-2 px-2 scrollbar-none">
          {items.map((item, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              aria-label={`Пара ${i + 1}`}
              className={`relative w-28 md:w-36 lg:w-44 aspect-[16/10] flex-shrink-0 overflow-hidden border-2 transition-all duration-300 ${
                i === safeActive
                  ? "border-burgundy shadow-[4px_4px_0_0_#B8893E]"
                  : "border-burgundy/30 hover:border-burgundy opacity-60 hover:opacity-100"
              }`}
              data-testid={`ba-thumb-${i}`}
            >
              <img
                src={item.after}
                alt=""
                draggable={false}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <span
                className={`absolute bottom-1.5 left-1.5 font-heading text-base px-1.5 leading-tight ${
                  i === safeActive ? "bg-burgundy text-cream" : "bg-cream-paper text-burgundy"
                }`}
              >
                {String(i + 1).padStart(2, "0")}
              </span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BeforeAfter;
